import Component from '@glimmer/component';
import { on } from '@ember/modifier';

// ---------------------------------------------------------------------------
// Timeline – generic vertical timeline with customizable nodes and content
// ---------------------------------------------------------------------------
//
// **Timeline vs TimelineTracker — when to use which?**
//
// Timeline (this component):
//   - Generic, block-based — YOU control what renders in each node and content area
//   - Accepts any `@items` array (generic `<T>`)
//   - Best for: custom timelines where each node has different icons, colors, or
//     content — e.g., career milestones, activity feeds, changelogs
//
// TimelineTracker:
//   - Data-driven, opinionated — give it `@steps` with `state` and it handles all visuals
//   - Each step has a `title`, `state` (completed/active/upcoming), optional `date`,
//     `description`, and `icon`
//   - Best for: status/progress trackers with a fixed state pattern — e.g., application
//     progress, loan processing stages, onboarding flows
//
// Rule of thumb: if your timeline has a clear "done / in-progress / not-started"
// progression, use TimelineTracker. If each item is unique, use Timeline.
// ---------------------------------------------------------------------------

interface TimelineSignature<T = unknown> {
  Args: {
    /** Array of items to render in the timeline */
    items: T[];
    /** Custom class for the connector lines between nodes (color/gradient) */
    lineClass?: string;
    /** Custom class for the list container */
    listClass?: string;
    /** Custom class for each item row */
    itemClass?: string;
    /** Custom class for the node wrapper */
    nodeClass?: string;
    /** Custom class for the content container */
    contentClass?: string;
    /** Custom class for connector positioning (default: 'left-[11px]') */
    connectorClass?: string;
    /** When provided, the connector between two adjacent items becomes clickable, called
     * with (itemAbove, itemBelow, index). */
    onConnectorClick?: (itemAbove: T, itemBelow: T, index: number) => void;
    /** Accessible label for the clickable connector (used with onConnectorClick). */
    connectorLabel?: string;
    /** When provided, each node becomes clickable, called with (item, index). */
    onNodeClick?: (item: T, index: number) => void;
    /** Accessible label for a clickable node (used with onNodeClick). */
    nodeLabel?: string;
    /** Whether a node is currently selected — drives `aria-pressed` / `data-selected` on the
     * clickable node button. Only takes effect when `@onNodeClick` is provided (without it the
     * node renders as a plain `<div>` with no selection attributes). */
    isNodeSelected?: (item: T, index: number) => boolean;
  };
  Blocks: {
    node?: [item: T, index: number];
    content?: [item: T, index: number];
    default?: [item: T, index: number];
  };
  Element: HTMLDivElement;
}

/** Check if this is the last item in the array */
function isLastItem(index: number, arr: unknown[]) {
  return index === arr.length - 1;
}

export default class Timeline<T = unknown> extends Component<TimelineSignature<T>> {
  get lineClass() {
    return this.args.lineClass ?? 'tio-timeline__line';
  }

  get listClass() {
    return this.args.listClass ?? '';
  }

  get itemClass() {
    return this.args.itemClass ?? 'pb-6';
  }

  get nodeClass() {
    return this.args.nodeClass ?? '';
  }

  get contentClass() {
    return this.args.contentClass ?? 'tio-fill-min';
  }

  get connectorClass() {
    return this.args.connectorClass ?? 'left-[11px]';
  }

  handleConnectorClick = (index: number) => {
    const itemAbove = this.args.items[index];
    const itemBelow = this.args.items[index + 1];
    if (itemAbove === undefined || itemBelow === undefined) return;

    this.args.onConnectorClick?.(itemAbove, itemBelow, index);
  };

  handleNodeClick = (index: number) => {
    const item = this.args.items[index];
    if (item === undefined) return;

    this.args.onNodeClick?.(item, index);
  };

  nodeSelected = (item: T, index: number) => this.args.isNodeSelected?.(item, index) ?? false;

  <template>
    <div data-test-timeline class="relative" ...attributes>
      <div class="{{this.listClass}}">
        {{#each @items as |item index|}}
          <div data-test-timeline-item class="flex gap-3 relative {{this.itemClass}}">
            {{! Connector line between nodes (skip for last item) }}
            {{#unless (isLastItem index @items)}}
              <div
                data-test-timeline-connector
                class="absolute {{this.connectorClass}} top-6 bottom-0 w-0.5 {{this.lineClass}}"
              ></div>
              {{#if @onConnectorClick}}
                <button
                  type="button"
                  data-test-timeline-connector-button
                  class="tio-timeline__connector-button absolute left-[4px] top-6 bottom-0 w-4 z-10 cursor-pointer rounded hover:tio-bg-brand-soft"
                  aria-label={{if @connectorLabel @connectorLabel "Compare adjacent items"}}
                  {{on "click" (fn this.handleConnectorClick index)}}
                ></button>
              {{/if}}
            {{/unless}}

            {{! Node — clickable button when @onNodeClick is provided, plain div otherwise }}
            {{#if @onNodeClick}}
              <button
                type="button"
                data-test-timeline-node
                data-test-timeline-node-button
                class="tio-timeline__node-button relative shrink-0 z-10 cursor-pointer border-0 p-0 rounded-full
                  {{this.nodeClass}}"
                aria-label={{if @nodeLabel @nodeLabel "Select item"}}
                aria-pressed={{if
                  @isNodeSelected
                  (if (this.nodeSelected item index) "true" "false")
                }}
                data-selected={{if
                  @isNodeSelected
                  (if (this.nodeSelected item index) "true" "false")
                }}
                {{on "click" (fn this.handleNodeClick index)}}
              >
                {{#if (has-block "node")}}
                  {{yield item index to="node"}}
                {{else}}
                  <div class="w-6 h-6 rounded-full tio-bg-neutral"></div>
                {{/if}}
              </button>
            {{else}}
              <div data-test-timeline-node class="relative shrink-0 z-10 {{this.nodeClass}}">
                {{#if (has-block "node")}}
                  {{yield item index to="node"}}
                {{else}}
                  <div class="w-6 h-6 rounded-full tio-bg-neutral"></div>
                {{/if}}
              </div>
            {{/if}}

            {{! Content }}
            <div data-test-timeline-content class="{{this.contentClass}}">
              {{#if (has-block "content")}}
                {{yield item index to="content"}}
              {{else}}
                {{yield item index}}
              {{/if}}
            </div>
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
